import { doGET } from '../../utils/HttpUtils';
import { ExternalCatalogIntegration } from '../../types/externalCatalog';

export type DiagnosticLocation = { pincode?: string; latitude?: number; longitude?: number };

export type DiagnosticPackageOption = {
    id: string;
    name: string;
    price?: number;
    packageType?: string;
};

export type DiagnosticLab = {
    id: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    packageType?: string;
    latitude?: number;
    longitude?: number;
    distanceKm?: number;
};

export type DiagnosticServiceabilityResult = {
    serviceable: boolean;
    labs: DiagnosticLab[];
};

const asRecord = (value: unknown): Record<string, any> =>
    value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {};

const firstValue = (source: Record<string, any>, keys: string[]) =>
    keys.map(key => source[key]).find(value => value !== undefined && value !== null && value !== '');

const firstNumber = (...values: unknown[]): number | undefined => {
    const value = values.find(item => item !== undefined && item !== null && item !== '');
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeDistanceKm = (value: unknown): number | undefined => {
    const parsed = firstNumber(value);
    if (parsed === undefined) return undefined;
    return parsed > 100 ? parsed / 1000 : parsed;
};

export function calculateDistanceKm(location: DiagnosticLocation, latitude?: number, longitude?: number): number | undefined {
    if (location.latitude === undefined || location.longitude === undefined || latitude === undefined || longitude === undefined) return undefined;
    const radians = Math.PI / 180;
    const dLat = (latitude - location.latitude) * radians;
    const dLng = (longitude - location.longitude) * radians;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(location.latitude * radians) *
        Math.cos(latitude * radians) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function parseDiagnosticLocation(mode: string, pincode: string, latitude: string, longitude: string): DiagnosticLocation {
    if (mode === 'pincode') {
        if (!/^[1-9]\d{5}$/.test(pincode.trim())) throw new Error('Enter a valid 6-digit PIN code.');
        return { pincode: pincode.trim() };
    }
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!latitude.trim() || !longitude.trim() || !Number.isFinite(lat) || !Number.isFinite(lng) ||
        lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        throw new Error('Enter latitude between -90 and 90 and longitude between -180 and 180.');
    }
    return { latitude: lat, longitude: lng };
}

const unwrap = (response: any) => {
    if (response?.error || response?.status >= 400) {
        throw new Error(response?.message || 'Unable to check this provider. Please try again.');
    }
    return response?.data?.data ?? response?.data;
};

export async function fetchDiagnosticPackages(integration: ExternalCatalogIntegration): Promise<DiagnosticPackageOption[]> {
    const response = await doGET(`/external-bookings/packages?integration=${integration}`);
    const data = unwrap(response);
    if (!Array.isArray(data)) throw new Error('The package list returned an invalid response. Please try again.');
    return data.map(item => ({
        id: String(item?.id || ''),
        name: String(item?.name || item?.packageName || item?.id || 'Package'),
        price: typeof item?.price === 'number' ? item.price : undefined,
        packageType: item?.packageType,
    })).filter(item => item.id);
}

const extractLabs = (payload: unknown): Record<string, any>[] => {
    const root = asRecord(payload);
    const data = asRecord(root.data);
    const result = asRecord(root.result);
    for (const source of [root, data, result]) {
        for (const key of ['labs', 'providers']) {
            if (Array.isArray(source[key])) return source[key].map(asRecord);
        }
    }
    return [];
};

const mapLab = (item: Record<string, any>, index: number, integration: ExternalCatalogIntegration, location: DiagnosticLocation): DiagnosticLab => {
    const meta = asRecord(item.meta);
    const providerLatLong = asRecord(item.providerLatLong || meta.providerLatLong);
    const coordinates = item.coordinates || item.location?.coordinates || meta.coordinates;
    const latitude = firstNumber(
        item.latitude, item.lat, item.labLat, item.lablat, item.providerLatitude,
        providerLatLong.latitude, providerLatLong.lat, Array.isArray(coordinates) ? coordinates[1] : undefined,
    );
    const longitude = firstNumber(
        item.longitude, item.lng, item.long, item.labLong, item.lablong, item.providerLongitude,
        providerLatLong.longitude, providerLatLong.lng, providerLatLong.long,
        Array.isArray(coordinates) ? coordinates[0] : undefined,
    );
    const providerDistance = normalizeDistanceKm(item.distance ?? meta.distance);
    const calculatedDistance = calculateDistanceKm(location, latitude, longitude);

    return {
        id: String(firstValue(item, ['id', 'labId', 'lsLabId', 'providerId']) ?? index),
        name: String(firstValue(item, ['name', 'labName', 'providerName']) ?? 'Diagnostic lab'),
        address: firstValue(item, ['address', 'providerAddress', 'labAddress', 'fullAddress']),
        city: firstValue(item, ['city']),
        state: firstValue(item, ['state']),
        pincode: firstValue(item, ['pincode', 'zipCode'])?.toString(),
        packageType: item.packageType,
        latitude,
        longitude,
        distanceKm: integration === 'betacura' ? calculatedDistance : providerDistance ?? calculatedDistance,
    };
};

export function normalizeDiagnosticServiceability(
    integration: ExternalCatalogIntegration,
    payload: unknown,
    location: DiagnosticLocation,
): DiagnosticServiceabilityResult {
    const root = asRecord(payload);
    let labs = extractLabs(root).map((item, index) => mapLab(item, index, integration, location));

    if (integration === 'betacura' && location.pincode) {
        labs = labs.filter(lab => lab.pincode === location.pincode);
    }

    labs.sort((a, b) => (a.distanceKm ?? Number.MAX_SAFE_INTEGER) - (b.distanceKm ?? Number.MAX_SAFE_INTEGER) || a.name.localeCompare(b.name));
    const explicitServiceable = firstValue(root, ['serviceable', 'isServiceable']);
    return {
        serviceable: typeof explicitServiceable === 'boolean' ? explicitServiceable : labs.length > 0,
        labs,
    };
}

export async function fetchDiagnosticServiceability(
    integration: ExternalCatalogIntegration,
    packageId: string,
    location: DiagnosticLocation,
): Promise<DiagnosticServiceabilityResult> {
    const query = new URLSearchParams({ integration, packageIds: packageId });
    Object.entries(location).forEach(([key, value]) => query.set(key, String(value)));
    const response = await doGET(`/external-bookings/serviceability?${query.toString()}`);
    return normalizeDiagnosticServiceability(integration, unwrap(response), location);
}
